import React, { useEffect, useState } from 'react';
import {
  Activity, BarChart3, Banknote, CalendarClock, ClipboardList, Clock, CopyCheck, FileClock, FlaskConical, HeartPulse, Hourglass, IdCard, LineChart,
  BookUser, CalendarDays, CalendarOff, MapPin, Navigation, Package, PenLine, Pill, PillBottle, Receipt, Repeat, Stethoscope, Syringe, Truck, Users, UserPlus, UserSearch, Wallet,
  CircleSlash, PackageCheck, Scale, Telescope, TrendingUp,
} from 'lucide-react';
import DailyCollectionReport from './DailyCollectionReport';
import RevenueTrendReport from './RevenueTrendReport';
import FinancialOutlookReport from './FinancialOutlookReport';
import RevenueByServiceReport from './RevenueByServiceReport';
import ReceivablesReport from './ReceivablesReport';
import { FEATURES } from '../../config/features';
import TopPayersReport from './TopPayersReport';
import NotCompletedSameDayReport from './NotCompletedSameDayReport';
import VisitTrendsReport from './VisitTrendsReport';
import DoctorLoadReport from './DoctorLoadReport';
import TokenEditsReport from './TokenEditsReport';
import UnfinishedVisitsReport from './UnfinishedVisitsReport';
import VitalsEditsReport from './VitalsEditsReport';
import NewVsReturningReport from './NewVsReturningReport';
import AgeGenderReport from './AgeGenderReport';
import SharedCnicReport from './SharedCnicReport';
import DuplicatePatientsReport from './DuplicatePatientsReport';
import TopDiagnosesReport from './TopDiagnosesReport';
import TopMedicinesReport from './TopMedicinesReport';
import TopLabTestsReport from './TopLabTestsReport';
import StockValuationReport from './StockValuationReport';
import ConsumptionReport from './ConsumptionReport';
import ExpiryWatchReport from './ExpiryWatchReport';
import VendorPurchasesReport from './VendorPurchasesReport';
import SuppliesGivenReport from './SuppliesGivenReport';
import PriceChangesReport from './PriceChangesReport';
import GenericPricesReport from './GenericPricesReport';
import OrderDeliveriesReport from './OrderDeliveriesReport';
import PatientProfileReport from './patientProfile/PatientProfileReport';
import MedicineProfileReport from './medicineProfile/MedicineProfileReport';
import FrequentPatientsReport from './frequentPatients/FrequentPatientsReport';
import PatientRegisterReport from './patientRegister/PatientRegisterReport';
import AttendanceRegisterReport from './attendance/AttendanceRegisterReport';
import AttendanceSummaryReport from './attendance/AttendanceSummaryReport';
import AttendanceFlaggedReport from './attendance/AttendanceFlaggedReport';
import LateArrivalsReport from './attendance/LateArrivalsReport';
import PunchLocationsReport from './attendance/PunchLocationsReport';
import LeaveBalancesReport from './attendance/LeaveBalancesReport';
import PayrollReport from './attendance/PayrollReport';
import { scrollContentToTop } from '../../utils/scroll';

// The Reporting module shell. The sidebar picks the GROUP (via defaultTab); the pills below the
// header pick the report within it. Each report is its own component fetching its own endpoint,
// so switching reports never drags another report's data along.

const GROUPS = {
  finance: {
    label: 'Finance Reports',
    subtitle: 'What the hospital earns, is owed, and collects',
    reports: [
      { id: 'daily-collection', label: 'Daily Collection', icon: Banknote, component: DailyCollectionReport },
      { id: 'revenue-trend', label: 'Revenue Trend', icon: LineChart, component: RevenueTrendReport },
      { id: 'financial-outlook', label: 'Financial Outlook', icon: Telescope, component: FinancialOutlookReport },
      { id: 'revenue-by-service', label: 'By Service & Item', icon: Receipt, component: RevenueByServiceReport },
      // Parked with the FINALIZED invoice state it queries; see FEATURES.receivablesReport.
      ...(FEATURES.receivablesReport
        ? [{ id: 'receivables', label: 'Receivables', icon: Hourglass, component: ReceivablesReport }]
        : []),
      { id: 'top-payers', label: 'Top Payers', icon: Wallet, component: TopPayersReport },
      { id: 'not-completed-same-day', label: 'Not Completed Same Day', icon: CalendarClock, component: NotCompletedSameDayReport },
      // Carries salaries, so it needs its own permission on top of the Finance group's.
      { id: 'payroll', label: 'Payroll', icon: Wallet, component: PayrollReport, action: 'VIEW_PAYROLL' },
    ],
  },
  attendance: {
    label: 'Attendance Reports',
    subtitle: 'Who was in, who was late, where they marked it from, and what leave is left',
    reports: [
      { id: 'attendance-register', label: 'Attendance Register', icon: CalendarDays, component: AttendanceRegisterReport },
      { id: 'attendance-summary', label: 'Attendance Summary', icon: Activity, component: AttendanceSummaryReport },
      // These two put a member of staff on a map, which is a different thing from knowing they were
      // late, so they need the Staff Locations permission on top of the Attendance group.
      { id: 'attendance-flagged', label: 'Flagged Punches', icon: MapPin, component: AttendanceFlaggedReport, action: 'VIEW_STAFF_LOCATIONS' },
      { id: 'late-arrivals', label: 'Late Arrivals', icon: Clock, component: LateArrivalsReport },
      { id: 'leave-balances', label: 'Leave Balances', icon: CalendarOff, component: LeaveBalancesReport },
      { id: 'punch-locations', label: 'Punch Locations', icon: Navigation, component: PunchLocationsReport, action: 'VIEW_STAFF_LOCATIONS' },
    ],
  },
  opd: {
    label: 'OPD & Patient Reports',
    subtitle: 'Visit volume, doctor load, and who the hospital serves',
    reports: [
      { id: 'visit-trends', label: 'Visit Trends', icon: Activity, component: VisitTrendsReport },
      { id: 'doctor-load', label: 'Doctor Load', icon: Stethoscope, component: DoctorLoadReport },
      { id: 'new-vs-returning', label: 'New vs Returning', icon: UserPlus, component: NewVsReturningReport },
      { id: 'frequent-patients', label: 'Frequent Patients', icon: Repeat, component: FrequentPatientsReport },
      { id: 'age-gender', label: 'Age & Gender', icon: Users, component: AgeGenderReport },
      { id: 'shared-cnic', label: 'Shared CNIC', icon: IdCard, component: SharedCnicReport },
      { id: 'duplicate-patients', label: 'Potential Duplicates', icon: CopyCheck, component: DuplicatePatientsReport },
      { id: 'token-edits', label: 'Token Edits', icon: PenLine, component: TokenEditsReport },
      { id: 'unfinished-visits', label: 'Unfinished Visits', icon: CircleSlash, component: UnfinishedVisitsReport },
      // Lists every patient on opening, so it needs its own permission on top of the group's.
      { id: 'patient-register', label: 'Patient Register', icon: BookUser, component: PatientRegisterReport, action: 'VIEW_PATIENT_REGISTER' },
      { id: 'patient-profile', label: 'Patient Profile', icon: UserSearch, component: PatientProfileReport },
    ],
  },
  clinical: {
    label: 'Clinical Reports',
    subtitle: 'What gets diagnosed, prescribed, and ordered',
    reports: [
      { id: 'top-diagnoses', label: 'Top Diagnoses', icon: HeartPulse, component: TopDiagnosesReport },
      { id: 'top-medicines', label: 'Top Medicines', icon: Pill, component: TopMedicinesReport },
      { id: 'top-lab-tests', label: 'Top Lab Tests', icon: FlaskConical, component: TopLabTestsReport },
      { id: 'vitals-edits', label: 'Vitals Edits', icon: FileClock, component: VitalsEditsReport },
    ],
  },
  inventory: {
    label: 'Inventory Reports',
    subtitle: 'Stock value, movement, expiry, and purchasing',
    reports: [
      { id: 'stock-valuation', label: 'Stock Valuation', icon: Package, component: StockValuationReport },
      { id: 'consumption', label: 'Consumption', icon: ClipboardList, component: ConsumptionReport },
      { id: 'expiry-watch', label: 'Expiry Watch', icon: Hourglass, component: ExpiryWatchReport },
      { id: 'vendor-purchases', label: 'Vendor Purchases', icon: Truck, component: VendorPurchasesReport },
      { id: 'order-deliveries', label: 'Orders vs Deliveries', icon: PackageCheck, component: OrderDeliveriesReport },
      { id: 'price-changes', label: 'Price Changes', icon: TrendingUp, component: PriceChangesReport },
      { id: 'generic-prices', label: 'Generic Prices', icon: Scale, component: GenericPricesReport },
      { id: 'supplies-given', label: 'Supplies Given', icon: Syringe, component: SuppliesGivenReport },
      { id: 'medicine-profile', label: 'Medicine Profile', icon: PillBottle, component: MedicineProfileReport },
    ],
  },
};

const ReportsManagement = ({ defaultTab, hasPermission }) => {
  const groupKey = GROUPS[defaultTab] ? defaultTab : 'finance';
  const group = GROUPS[groupKey];
  const [activeReport, setActiveReport] = useState(group.reports[0].id);

  // The sidebar owns the group; when it changes, land on that group's first report.
  useEffect(() => {
    setActiveReport((GROUPS[defaultTab] || GROUPS.finance).reports[0].id);
  }, [defaultTab]);

  // Re-clicking the sidebar item snaps back to the group's first report.
  useEffect(() => {
    const onReset = () => setActiveReport(group.reports[0].id);
    window.addEventListener('clinic:module-reset', onReset);
    return () => window.removeEventListener('clinic:module-reset', onReset);
  }, [group]);

  // VIEW opens the module; each sidebar group has its own toggle, so an accounts user can be given
  // Finance without also seeing diagnoses in Clinical or patient identifiers in OPD.
  const GROUP_ACTION = {
    finance: 'VIEW_FINANCE_REPORTS',
    attendance: 'VIEW_ATTENDANCE_REPORTS',
    opd: 'VIEW_OPD_REPORTS',
    clinical: 'VIEW_CLINICAL_REPORTS',
    inventory: 'VIEW_INVENTORY_REPORTS',
  };
  const can = (action) =>
    typeof hasPermission === 'function' ? hasPermission('REPORTS', action) : true;
  const canView = can('VIEW') && can(GROUP_ACTION[groupKey] || 'VIEW');

  // A report may need a permission of its own beyond its group's (the Patient Register); one the
  // user lacks is not offered at all.
  const reports = group.reports.filter((r) => !r.action || can(r.action));
  const active = reports.find((r) => r.id === activeReport) || reports[0];
  const ActiveComponent = active?.component;

  // Holding a group whose every report needs a further permission leaves nothing to show. No group
  // is in that state today, but reading `active.component` off an empty list would take the page
  // down rather than say so.
  if (!canView || !ActiveComponent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
        <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-xl p-12 text-center">
          <BarChart3 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900">Reports</h2>
          <p className="text-gray-500 mt-2">
            {canView
              ? 'There is no report in this group you have permission to open.'
              : 'You do not have permission to view reports.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6 print:bg-white print:p-0">
      <div className="max-w-7xl mx-auto space-y-5">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
              <BarChart3 className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{group.label}</h1>
              <p className="text-gray-500">{group.subtitle}</p>
            </div>
          </div>
          {/* Report picker */}
          <div className="mt-5 flex flex-wrap gap-2">
            {reports.map((r) => {
              const Icon = r.icon;
              return (
                <button
                  key={r.id}
                  onClick={() => { setActiveReport(r.id); scrollContentToTop(); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    activeReport === r.id
                      ? 'bg-navy-800 text-white shadow'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* One report at a time - also what keeps the print layout unambiguous. */}
        <ActiveComponent key={active.id} hasPermission={hasPermission} />
      </div>
    </div>
  );
};

export default ReportsManagement;
